/**
 * Utility module: getMatchStats/getGoals.js.
 */

import { getAwayTeam, getHomeTeam } from './getTeams';

/** @typedef {import('./types').Match} Match */
/** @typedef {import('./types').Goal} Goal */

/**
 * Internal helper that returns goals attributed to a given side.
 *
 * Own goals (`type === 'OG'`) are deliberately attributed to the *opposing*
 * team — that's how a soccer scoreboard counts them.
 *
 * @param {Match} match
 * @param {'home' | 'away'} side
 * @returns {Goal[]}
 */
const getGoalsForSide = (match, side) => {
  const goals = match?.liveData?.goal;
  if (!goals) return [];

  const home = getHomeTeam(match);
  const away = getAwayTeam(match);
  if (!home || !away) return [];

  const ownTeam = side === 'home' ? home : away;
  const otherTeam = side === 'home' ? away : home;

  return goals.filter((goal) => {
    const isOwnGoal = goal.type === 'OG';
    return isOwnGoal ?
        goal.contestantId === otherTeam.id
      : goal.contestantId === ownTeam.id;
  });
};

/**
 * Goals scored *for* the home team, including own goals by the away team.
 *
 * @param {Match} match
 * @returns {Goal[]}
 */
export const getHomeGoals = (match) => getGoalsForSide(match, 'home');

/**
 * Goals scored *for* the away team, including own goals by the home team.
 *
 * @param {Match} match
 * @returns {Goal[]}
 */
export const getAwayGoals = (match) => getGoalsForSide(match, 'away');
